/**
 * SRP-6a device-verifier helper for Cognito ConfirmDevice — mobile port.
 *
 * Direct port of backend/api/client/services/cognito/srp.js. The two MUST stay
 * byte-identical: both talk to the same user pool, and a verifier that differs
 * by a single byte is rejected. There is an equivalence test that runs this
 * source and the backend's against the same salt and password and compares.
 *
 * WHY MOBILE NEEDS THIS
 *
 * The user pool sets DeviceConfiguration (ChallengeRequiredOnNewDevice +
 * DeviceOnlyRememberedOnUserPrompt), so every successful InitiateAuth returns
 * NewDeviceMetadata (DeviceKey + DeviceGroupKey) and creates an UNCONFIRMED
 * device entry. Cognito then rejects REFRESH_TOKEN_AUTH from that refresh token
 * with NotAuthorizedException.
 *
 * apiClient's performRefresh classifies NotAuthorizedException as a definitive
 * auth error, calls handleAuthExpiry(), clears the tokens and dispatches
 * CLEAR_ALL — the login screen. Because the refresh is only attempted once the
 * 24h ID token has expired, this presents as "I opened the app the next day and
 * had to sign in again", every day, forever.
 *
 * The backend already solved this for the OAuth path (Cadence Mac client, whose
 * symptom was "every press of Refresh logs me out"). Mobile never got it.
 *
 * SRP-6a primer:
 *   N  = large safe-prime modulus (constant, 3072-bit from RFC 5054)
 *   g  = generator (constant, 2 for Cognito)
 *   x  = H(salt || H(DeviceGroupKey + DeviceKey + ":" + devicePassword))
 *   v  = g^x mod N            ← the password verifier
 *
 * The devicePassword is random and discarded after ConfirmDevice. Cognito only
 * stores the verifier, and the client never re-presents the password because
 * REFRESH_TOKEN_AUTH from a confirmed device skips device-password verification.
 */

import { sha256 } from '@noble/hashes/sha2.js';
import { Buffer } from 'buffer';
import * as Crypto from 'expo-crypto';

// AWS Cognito SRP group: 3072-bit safe prime + generator 2.
// Verbatim from amazon-cognito-identity-js (Apache-2.0), same as the backend.
const N_HEX = (
  'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD1' +
  '29024E088A67CC74020BBEA63B139B22514A08798E3404DD' +
  'EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245' +
  'E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
  'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3D' +
  'C2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F' +
  '83655D23DCA3AD961C62F356208552BB9ED529077096966D' +
  '670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
  'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9' +
  'DE2BCBF6955817183995497CEA956AE515D2261898FA0510' +
  '15728E5A8AAAC42DAD33170D04507A33A85521ABDF1CBA64' +
  'ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7' +
  'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6B' +
  'F12FFA06D98A0864D87602733EC86A64521F2B18177B200C' +
  'BBE117577A615D6C770988C0BAD946E208E24FA074E5AB31' +
  '43DB5BF1A621D6B23873C2EBD4B5BAE2EB91D8B5E61B9AC8'
).toLowerCase();

const N_BI = BigInt('0x' + N_HEX);
const G_BI = BigInt(2);
const ZERO = BigInt(0);
const ONE = BigInt(1);

/**
 * modpow — square-and-multiply. ~256 iterations for the 256-bit exponent that
 * SHA-256 produces. Hermes supports BigInt but is slower at it than V8; callers
 * should invoke it only when Cognito returns new-device metadata.
 */
function modPow(base: bigint, exp: bigint, mod: bigint): bigint {
  let result = ONE;
  let b = base % mod;
  let e = exp;
  while (e > ZERO) {
    if (e & ONE) result = (result * b) % mod;
    e >>= ONE;
    b = (b * b) % mod;
  }
  return result;
}

const toHex = (bytes: Uint8Array): string => Buffer.from(bytes).toString('hex');

/** SHA-256 over a utf8 string or raw bytes, hex out. Mirrors the backend helper. */
function sha256Hex(input: Uint8Array | string): string {
  const bytes = typeof input === 'string' ? Uint8Array.from(Buffer.from(input, 'utf8')) : input;
  return toHex(sha256(bytes));
}

/**
 * Hex → bytes, with Cognito's sign-bit guard.
 *
 * Cognito's Java handler parses both PasswordVerifier and Salt as a SIGNED
 * BigInteger after base64-decoding. If the first byte's high bit is set, Java
 * reads the value as negative and rejects the call with
 * `InvalidParameterException: Found negative value for salt or password
 * verifier.` — which hit roughly half of all random salts when the backend
 * version was first written (observed 2026-05-22).
 *
 * Prepending 0x00 when the high bit is set keeps the re-parsed BigInteger
 * positive. Match this exactly or the verifier will not validate.
 */
function hexToBytes(hex: string): Uint8Array {
  let h = hex;
  if (h.length % 2 !== 0) h = '0' + h;
  if (parseInt(h.charAt(0), 16) >= 8) h = '00' + h;
  return Uint8Array.from(Buffer.from(h, 'hex'));
}

export interface DeviceSecretVerifierConfig {
  PasswordVerifier: string;
  Salt: string;
}

/**
 * The pure core: derive a verifier from a KNOWN salt and password.
 *
 * Split out from the random generation so the equivalence test can drive this
 * and the backend with identical inputs and compare byte for byte. Never call
 * this directly with a fixed password in production.
 */
export function deriveDeviceVerifier(
  deviceGroupKey: string,
  deviceKey: string,
  devicePassword: string,
  saltHex: string,
): DeviceSecretVerifierConfig {
  // x = SHA256(salt || SHA256(deviceGroupKey + deviceKey + ":" + devicePassword))
  const fullPassword = `${deviceGroupKey}${deviceKey}:${devicePassword}`;
  const innerHashHex = sha256Hex(fullPassword);
  const saltedHashHex = sha256Hex(hexToBytes(saltHex + innerHashHex));
  const x = BigInt('0x' + saltedHashHex);

  // v = g^x mod N
  const verifierBI = modPow(G_BI, x, N_BI);

  let verifierHex = verifierBI.toString(16);
  if (verifierHex.length % 2 !== 0) verifierHex = '0' + verifierHex;

  return {
    PasswordVerifier: Buffer.from(hexToBytes(verifierHex)).toString('base64'),
    Salt: Buffer.from(hexToBytes(saltHex)).toString('base64'),
  };
}

/**
 * Generate a fresh device-verifier config for Cognito ConfirmDevice.
 *
 * @returns devicePassword (retained only for the one ConfirmDevice call, never
 *   persisted) and the verifier config Cognito stores.
 */
export async function createDeviceVerifier(
  deviceGroupKey: string,
  deviceKey: string,
): Promise<{ devicePassword: string; deviceSecretVerifierConfig: DeviceSecretVerifierConfig }> {
  // 40 random bytes, base64. Device passwords have no policy floor; this is
  // generous so a future SRP-recompute path has plenty of entropy.
  const devicePassword = Buffer.from(await Crypto.getRandomBytesAsync(40)).toString('base64');

  // 128-bit salt, padded to 256-bit hex for Cognito's SRP interop.
  const saltHex = Buffer.from(await Crypto.getRandomBytesAsync(16)).toString('hex').padStart(32, '0');

  return {
    devicePassword,
    deviceSecretVerifierConfig: deriveDeviceVerifier(deviceGroupKey, deviceKey, devicePassword, saltHex),
  };
}

export const _internal = { modPow, sha256Hex, hexToBytes, N_HEX };
